/**
 * ZoningMap — Leaflet map with optional parcel pin and zoning GeoJSON overlay.
 */
import React, { useMemo } from 'react';
import {
  MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Data-URI icons — avoid CDN fetches blocked by page CSP.
const MARKER_ICON = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">'
  + '<path fill="#1565c0" stroke="#0d47a1" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>'
  + '<circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>',
);
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: MARKER_ICON,
  iconRetinaUrl: MARKER_ICON,
  shadowUrl: '',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const onEachFeature = (feature, layer) => {
  const props = feature?.properties || {};
  layer.bindPopup(
    `<strong>${props.zone || 'Zone'}</strong><br/>${props.name || ''}`,
  );
};

const PLACEHOLDER_GEOJSON = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: { zone: 'R-1', name: 'Residential Single Family' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-122.4194, 37.7749],
          [-122.418, 37.7749],
          [-122.418, 37.776],
          [-122.4194, 37.776],
          [-122.4194, 37.7749],
        ]],
      },
    },
    {
      type: 'Feature',
      properties: { zone: 'C-2', name: 'Commercial' },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-122.418, 37.7749],
          [-122.4165, 37.7749],
          [-122.4165, 37.776],
          [-122.418, 37.776],
          [-122.418, 37.7749],
        ]],
      },
    },
  ],
};

const DEFAULT_CENTER = [37.7749, -122.4194];
const DEFAULT_ZOOM = 14;

function SetViewOnSelect({ center, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (center) map.setView(center, zoom ?? DEFAULT_ZOOM);
  }, [center, zoom, map]);
  return null;
}

function zoneStyle(feature) {
  const zone = feature?.properties?.zone || 'unknown';
  const colors = {
    'R-1': '#90EE90', 'C-2': '#87CEEB', 'M-1': '#DDA0DD', default: '#cccccc',
  };
  return {
    fillColor: colors[zone] || colors.default,
    fillOpacity: 0.5,
    color: '#333',
    weight: 1,
  };
}

/**
 * @param {{
 *   center?: [number, number],
 *   zoom?: number,
 *   geoJson?: object|null,
 *   height?: number,
 *   marker?: { lat: number, lng: number, label?: string }|null,
 *   showPlaceholderOverlay?: boolean,
 * }} props
 */
export default function ZoningMap({
  center,
  zoom = DEFAULT_ZOOM,
  geoJson,
  height = 400,
  marker = null,
  showPlaceholderOverlay = true,
}) {
  const data = useMemo(() => {
    if (geoJson) return geoJson;
    if (showPlaceholderOverlay && !marker) return PLACEHOLDER_GEOJSON;
    return null;
  }, [geoJson, showPlaceholderOverlay, marker]);

  const mapCenter = center
    || (marker ? [marker.lat, marker.lng] : DEFAULT_CENTER);

  return (
    <MapContainer
      center={mapCenter}
      zoom={zoom}
      style={{ height, width: '100%', borderRadius: 8 }}
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19}
      />
      <SetViewOnSelect center={mapCenter} zoom={zoom} />
      {data && (
        <GeoJSON data={data} style={zoneStyle} onEachFeature={onEachFeature} />
      )}
      {marker?.lat != null && marker?.lng != null && (
        <Marker position={[marker.lat, marker.lng]}>
          <Popup>{marker.label || 'Parcel'}</Popup>
        </Marker>
      )}
    </MapContainer>
  );
}
